// src/components/Layout/Layout.jsx
import React, { useState } from "react";
import { Box, CssBaseline, Drawer, Menu, MenuItem, Divider, Toolbar, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Dashboard, Hotel, Payment, ReportProblem, People, AccountCircle, Logout, Settings, Visibility, QrCodeScanner } from "@mui/icons-material";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSocket } from "../../context/SocketContext";

const drawerWidth = 260;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { notifications } = useSocket();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Navigation items based on your abstract requirements
  const menuItems = [
    {
      text: "Dashboard",
      icon: <Dashboard />,
      path: "/dashboard",
      roles: ["student", "warden", "admin"],
      description: "Real-time analytics & overview",
    },
    {
      text: "Rooms",
      icon: <Hotel />,
      path: "/rooms",
      roles: ["warden", "admin"],
      description: "Real-time room availability",
    },
    {
      text: "Payments",
      icon: <Payment />,
      path: "/payments",
      roles: ["student", "warden", "admin"],
      description: "Secure PhonePe payment system",
    },
    {
      text: "Complaints",
      icon: <ReportProblem />,
      path: "/complaints",
      roles: ["student", "warden", "admin"],
      description: "Digital complaint management",
    },
    {
      text: "Users",
      icon: <People />,
      path: "/users",
      roles: ["warden", "admin"],
      description: "User management",
    },
    // Removed Visitors and Entry/Exit items per design request
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.includes(user?.role)
  );

  // Sidebar content is rendered via Sidebar component

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Header
        onMenuClick={handleDrawerToggle}
        onProfileOpen={handleProfileMenuOpen}
        onNotificationOpen={handleNotificationOpen}
        notifications={notifications}
        user={user}
        isMobile={isMobile}
      />

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          sx: { mt: 1.5, minWidth: 200 },
        }}
      >
        <MenuItem onClick={() => navigate("/profile")}>
          <AccountCircle sx={{ mr: 2 }} />
          My Profile
        </MenuItem>
        <MenuItem onClick={() => navigate("/settings")}>
          <Settings sx={{ mr: 2 }} />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
          <Logout sx={{ mr: 2 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: { mt: 1.5, maxWidth: 350, maxHeight: 400 },
        }}
      >
        {notifications.length > 0 ? (
          notifications.slice(0, 5).map((notification, index) => (
            <MenuItem key={index} onClick={handleNotificationClose}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {notification.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.message}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem>
            <Typography variant="body2" color="text.secondary">
              No new notifications
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          <Sidebar menuItems={filteredMenuItems} user={user} onNavigate={handleNavigation} mobileOpen={mobileOpen} onClose={handleDrawerToggle} />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          <Sidebar menuItems={filteredMenuItems} user={user} onNavigate={handleNavigation} />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          backgroundColor: "var(--surface-2)",
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
